import { useEffect, useState } from 'react';
import { ApiError, fetchMetadata, tokenFromPath } from './api';
import type { FileMetadata } from './types';
import { ViewerFrame } from './components/ViewerFrame';
import { LoadingPanel } from './components/LoadingPanel';
import { ErrorPanel } from './components/ErrorPanel';
import { UnknownRoutePanel } from './components/UnknownRoutePanel';

type Phase =
  | { state: 'parsing' }
  | { state: 'loading'; token: string }
  | { state: 'ready'; token: string; metadata: FileMetadata }
  | { state: 'error'; token: string | null; error: ApiError }
  | { state: 'unknown-route' };

export function App() {
  const [phase, setPhase] = useState<Phase>({ state: 'parsing' });

  useEffect(() => {
    const token = tokenFromPath(window.location.pathname);
    if (!token) {
      setPhase({ state: 'unknown-route' });
      return undefined;
    }

    const controller = new AbortController();
    setPhase({ state: 'loading', token });

    fetchMetadata(token, controller.signal)
      .then((metadata) => {
        setPhase({ state: 'ready', token, metadata });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        const apiErr =
          err instanceof ApiError
            ? err
            : new ApiError(0, 'CLIENT_ERROR', `unexpected: ${(err as Error).message}`);
        setPhase({ state: 'error', token, error: apiErr });
      });

    return () => {
      controller.abort();
    };
  }, []);

  switch (phase.state) {
    case 'parsing':
    case 'loading':
      return <LoadingPanel />;
    case 'ready':
      return <ViewerFrame token={phase.token} metadata={phase.metadata} />;
    case 'error':
      return <ErrorPanel error={phase.error} />;
    case 'unknown-route':
      return <UnknownRoutePanel />;
  }
}
